import { isNil, map, pick, pickBy, values, filter } from 'lodash';
import { createSelector } from 'reselect';
import { getCurrentUser } from '../session';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { isDiscoverable } from 'new/utils/nexsetUtils';

export const getUserEntities = (state) => {
  return state.entities.users;
};

export const getOrgEntities = (state) => {
  return state.entities.orgs;
};

/* Sources */
const getSourceIds = (state) => state.sources.byId;

const getSourceEntities = (state) => state.entities.sources;

export const getEligibleSourceEntities = createSelector(
  [getSourceIds, getSourceEntities],
  (sourceIds, sourceEntities) => map(sourceIds, (id) => sourceEntities[id]),
);

export const getSourceEntityById = (state, id) => {
  return getSourceEntities(state).get(id);
};

/* Sinks */
const getSinkIds = (state) => state.sinks.byId;

const getSinkEntities = (state) => state.entities.sinks;

export const getEligibleSinkEntities = createSelector(
  [getSinkIds, getSinkEntities],
  (sinkIds, sinkEntities) => {
    return map(sinkIds, (id) => sinkEntities[id]);
  },
);

export const getSinkEntityById = (state, id) => {
  return getSinkEntities(state).get(id);
};

/* Datasets */

export const getDataSetEntities = (state) => state.entities.dataSets;

export const getDataSetEntityById = (state, id) => {
  return getDataSetEntities(state).get(id);
};

/* My Datasets */
const getMyDataSetIds = (state) => state.dataSets.byId;

export const getMyDataSetEntities = createSelector(
  [getMyDataSetIds, getDataSetEntities],
  (myDatasetIds, dataSetEntities) => {
    // Filter to only include discoverable nexsets in list views
    return filter(
      map(myDatasetIds, (id) => dataSetEntities[id]),
      (dataset) => dataset && isDiscoverable(dataset),
    );
  },
);

/* Available Datasets */
const getMyAvailableDataSetIds = (state) => {
  return [...state.sharedDatasets.byId, ...state.publicDatasets.byId];
};

//const getAvailableDataSetIds = (state) => state.sharedDatasets.byId;

export const getMyAvailableDatasetEntities = createSelector(
  [getMyAvailableDataSetIds, getDataSetEntities, getCurrentUser],
  (myAvailableDatasetIds, dataSets, currentUser) => {
    /*Not my Nexsets or public*/
    const validCurrentUser = !isNil(currentUser);
    const notMyDataSetEntities = pickBy(dataSets, (p) => {
      return p.public === true || (validCurrentUser === true && p.owner !== currentUser.id);
    });

    const availableDatasets = !isNil(notMyDataSetEntities)
      ? values(pick(notMyDataSetEntities, myAvailableDatasetIds))
      : [];

    // Filter to only include discoverable nexsets in list views
    return filter(availableDatasets, (dataset) => dataset && isDiscoverable(dataset));
  },
);

/* Public Datasets */
const getPublicDataSetIds = (state) => state.publicDatasets.byId;

export const getPublicDatasetEntities = createSelector(
  [getPublicDataSetIds, getDataSetEntities],
  (publicDatasetIds, dataSets) => {
    const publicDatasetEntites = pickBy(dataSets, (p) => {
      return p.public === true;
    });
    const publicDatasets = !isNil(publicDatasetEntites)
      ? map(publicDatasetIds, (id) => publicDatasetEntites[id])
      : [];

    // Filter to only include discoverable nexsets in list views
    return filter(publicDatasets, (dataset) => dataset && isDiscoverable(dataset));
  },
);

/* Lookups */
const getDataMapListing = (state) => state.dataMaps.listing;

export const getDataMapEntities = (state) => state.entities.dataMaps;

export const getEligibleDataMapEntities = createSelector(
  [getDataMapListing, getDataMapEntities],
  (dataMapListing, dataMapEntities) => {
    return map(dataMapListing, (id) => dataMapEntities[id]);
  },
);

/* Credentials */

const getCredentialIds = (state) => state.credentials.listing;
export const getCredentialEntities = (state) => state.entities.credentials;

export const getEligibleCredentialEntities = createSelector(
  [getCredentialIds, getCredentialEntities],
  (credentialIds, credentialEntities) => map(credentialIds, (id) => credentialEntities[id]),
);

export const getCredentialEntityById = (state, id) => {
  return getCredentialEntities(state).get(id);
};

/* Notifications */

export const getNotificationEntities = (state) => {
  return state.entities.notifications;
};

export const getNotificationEntityById = (state, id) => {
  return getNotificationEntities(state).get(id);
};

/* Transforms */

export const getTransformEntities = (state) => {
  return state.entities.transforms;
};

export const getTransformEntityById = (state, id) => {
  return getTransformEntities(state).get(id);
};

/* Schema Specs */

export const getSchemaSpecEntities = (state) => {
  return state.entities.schemaSpecs;
};

export const getSchemaSpecById = (state, id) => {
  return getSchemaSpecEntities(state).get(id);
};

/* API Keys */

export const getAPIKeyEntities = (state) => {
  return state.entities.apiKeys;
};

export const getAPIKeyById = (state, id) => {
  return getAPIKeyEntities(state).get(id);
};

/* Projects */

const getProjectIds = (state) => state.projects.listing;

const getProjectEntities = (state) => state.entities.projects;

export const getEligibleProjectEntitiesList = createSelector(
  [getProjectIds, getProjectEntities],
  (projectIds, projectEntities) => map(projectIds, (id) => projectEntities[id]),
);

const getEntities = (state) => state.entities;
const getRootNodeKeys = (state) => state.pipelines.rootNodeKeys;
const getSearchNodeKeys = (state) => state.pipelines.searchNodeKeys;

export const memoizedPipelinesSelector = createSelector(
  [(state) => state.pipelines.rootNodeKeys, (state) => state.entities],
  (rootNodeKeys, entities) => {
    const { flowNodes } = entities;
    return map(rootNodeKeys, (nodeKey) =>
      getResourceEntity(flowNodes[nodeKey], entities, true, ['name', 'description']),
    );
  },
);

export const getSearchFlows = createSelector(
  [getEntities, getSearchNodeKeys],
  (entities, searchNodeKeys) =>
    map(searchNodeKeys, (nodeKey) =>
      getResourceEntity(entities.flowNodes[nodeKey], entities, true, ['name', 'id', 'description']),
    ),
);

const getSqlAutomationsIds = (state) => state.flowCategories?.sqlAutomations?.ids ?? [];

export const sqlAutomationsPipelinesSelector = createSelector(
  [getSqlAutomationsIds, getEntities],
  (ids, entities) => {
    const { flowNodes } = entities;
    return map(ids, (nodeKey) =>
      getResourceEntity(flowNodes[nodeKey], entities, true, ['name', 'description']),
    );
  },
);

const getMcpFlowsIds = (state) => state.flowCategories?.mcpFlows?.ids ?? [];

export const memoizedMCPFlowsSelector = createSelector(
  [getMcpFlowsIds, getEntities],
  (ids, entities) => {
    const { flowNodes } = entities;
    return map(ids, (nodeKey) =>
      getResourceEntity(flowNodes[nodeKey], entities, true, ['name', 'description']),
    );
  },
);

/* Marketplace Domains */
const getDomainIds = (state) => state.domains.byId;
export const getAllDomainEntities = (state) => state.entities.domains;

export const getDomainEntitiesById = createSelector(
  [getDomainIds, getAllDomainEntities],
  (domainIds, domainEntities) => map(domainIds, (id) => domainEntities[id]),
);
